import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube } from "lucide-react";
import logo from '../../assets/logos/logo.png';
import { CONTACT_PHONE, CONTACT_EMAIL, COMPANY_ADDRESS, CONTACT_WHATSAPP } from "../../constants/data";

const GOLD = "#50C878";

export default function Footer() {
  return (
    <footer
      style={{
        background: "linear-gradient(180deg, #F7FBF8 0%, #F2F8F3 100%)",
        borderTop: "1px solid rgba(80,200,120,0.2)",
        padding: "60px 24px 32px",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
          gap: "60px", 
          marginBottom: "48px",
          padding: "0 20px"
        }}>
          {/* Brand */}
          <div>
            <div style={{ marginBottom: "16px" }}>
              <img 
                src={logo} 
                alt="GroupBuy Flats" 
                style={{
                  height: "50px",
                  width: "auto",
                  display: "block",
                  marginBottom: "16px",
                }}
              />
            </div>
            <p style={{ color: "rgba(1,50,32,0.62)", fontSize: "0.95rem", lineHeight: 1.8, maxWidth: "320px" }}>
              Hyderabad's first collective real estate buying platform. We aggregate buyer demand to negotiate the best deals with top builders.
            </p>
            <div style={{ display: "flex", gap: "14px", marginTop: "24px" }}>
              {[
                { 
                  icon: Instagram, 
                  href: "https://www.instagram.com/groupbuyflats/",
                  bg: "radial-gradient(circle at 30% 110%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
                  color: "white"
                },
                { 
                  icon: Facebook, 
                  href: "https://www.facebook.com/profile.php?id=61587953844726",
                  bg: "#1877F2",
                  color: "white"
                },
                { 
                  icon: null, 
                  href: CONTACT_WHATSAPP, 
                  isWhatsApp: true,
                  bg: "#25D366",
                  color: "white"
                },
              ].map(({ icon: Icon, href, isWhatsApp, bg, color }) => (
                <a 
                  key={href} 
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  style={{ 
                    width: "42px", 
                    height: "42px", 
                    borderRadius: "50%", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    textDecoration: "none", 
                    transition: "transform 0.2s, box-shadow 0.2s",
                    background: bg || "transparent",
                    border: isWhatsApp ? "none" : "none",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    color: color || GOLD,
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "scale(1.1)";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.2)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.12)";
                  }}
                >
                  {isWhatsApp ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  ) : (
                    <Icon size={18} />
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{ color: GOLD, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "24px", fontWeight: 600 }}>
              Quick Links
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px" }}>
              {[
                { label: "Home", path: "/" },
                { label: "About Us", path: "/about" },
                { label: "Why GroupBuy", path: "/why-groupbuy" },
                { label: "For Developers ", path: "/why-developers" },
                { label: "Projects", path: "/properties" },
                { label: "Developers OnBoard", path: "/developers" },
                { label: "Careers", path: "/careers" },
                { label: "Contact Us", path: "/contact" },
                { label: "FAQs", path: "/faqs" },
              ].map(({ label, path }) => (
                <Link key={path} to={path} style={{ display: "block", color: "rgba(1,50,32,0.62)", textDecoration: "none", fontSize: "0.95rem", marginBottom: "12px", transition: "color 0.2s" }}
                  onMouseOver={(e) => (e.currentTarget.style.color = GOLD)}
                  onMouseOut={(e) => (e.currentTarget.style.color = "rgba(1,50,32,0.62)")}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 style={{ color: GOLD, fontSize: "0.85rem", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "24px", fontWeight: 600 }}>
              Contact Us
            </h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <a href={`tel:+91${CONTACT_PHONE}`} style={{ display: "flex", alignItems: "flex-start", gap: "12px", color: "rgba(1,50,32,0.85)", textDecoration: "none", fontSize: "0.95rem" }}>
                <Phone size={18} color={GOLD} style={{ marginTop: "2px", flexShrink: 0 }} /> +91 {CONTACT_PHONE}
              </a>
              <a href={`mailto:${CONTACT_EMAIL}`} style={{ display: "flex", alignItems: "flex-start", gap: "12px", color: "rgba(1,50,32,0.85)", textDecoration: "none", fontSize: "0.95rem" }}>
                <Mail size={18} color={GOLD} style={{ marginTop: "2px", flexShrink: 0 }} /> {CONTACT_EMAIL}
              </a>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", color: "rgba(1,50,32,0.85)", fontSize: "0.95rem" }}>
                <MapPin size={18} color={GOLD} style={{ marginTop: "2px", flexShrink: 0 }} />
                <span>{COMPANY_ADDRESS}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ 
          borderTop: "1px solid rgba(80,200,120,0.15)", 
          paddingTop: "28px", 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          flexWrap: "wrap", 
          gap: "16px",
          padding: "28px 20px 0"
        }}>
          <p style={{ color: "rgba(1,50,32,0.48)", fontSize: "0.9rem" }}>
            © {new Date().getFullYear()} GroupBuy Flats. All rights reserved. | Powered by GroupBuy Flats Technology Platform.
          </p>
          <p style={{ color: "rgba(1,50,32,0.48)", fontSize: "0.88rem" }}>
            Designed & Developed By Zenvi Technologies
          </p>
        </div>
      </div>
    </footer>
  );
}